import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface TenantSetting {
  id: string;
  tenant_id: string;
  key: string;
  value: string;
  updated_at: string;
  updated_by: string | null;
}

export function useTenantSettings() {
  const { user, currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const settingsQuery = useQuery({
    queryKey: ["tenant-settings", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("tenant_settings" as any)
        .select("*")
        .eq("tenant_id", tenantId);
      if (error) throw error;
      return (data || []) as unknown as TenantSetting[];
    },
    enabled: !!tenantId,
  });

  const upsertSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      if (!tenantId) throw new Error("No tenant");

      // If value is empty, delete the setting instead of saving empty string
      if (!value || value.trim() === "") {
        const { error } = await supabase
          .from("tenant_settings" as any)
          .delete()
          .eq("tenant_id", tenantId)
          .eq("key", key);
        if (error) throw error;
        return;
      }

      const { data: existing } = await supabase
        .from("tenant_settings" as any)
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("key", key)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("tenant_settings" as any)
          .update({ value, updated_at: new Date().toISOString(), updated_by: user?.id || null } as any)
          .eq("tenant_id", tenantId)
          .eq("key", key);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("tenant_settings" as any)
          .insert({ tenant_id: tenantId, key, value, updated_by: user?.id || null } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-settings", tenantId] });
    },
  });

  const getSetting = (key: string, defaultValue = ""): string => {
    const setting = settingsQuery.data?.find((s) => s.key === key);
    return setting?.value ?? defaultValue;
  };

  const getBoolSetting = (key: string, defaultValue = true): boolean => {
    const val = getSetting(key, "");
    if (val === "") return defaultValue;
    return val === "true";
  };

  return {
    settings: settingsQuery.data || [],
    isLoading: settingsQuery.isLoading,
    getSetting,
    getBoolSetting,
    upsertSetting,
    refetch: settingsQuery.refetch,
  };
}
